import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { MultiSelect } from "@/components/ui/multiselect";
import { toast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";

import {
  fetchUsers,
  fetchDistrictUsers,
  fetchDistricts,
  updateUserStatus,
  setPagination,
  clearSuccess,
  clearError,
} from "@/store/slices/usersSlice";

import { Pagination } from "@/components/ui/pagination";
import { Edit } from "lucide-react";
import UserStatusModal from "./UserStatusModal";
import { exportUsers } from "../../store/slices/usersSlice";

const UserManagement = ({ roleList = "" }) => {
  const dispatch = useDispatch();
  const {
    users,
    district_users,
    districts,
    loading,
    statusUpdating,
    success,
    error,
    pagination,
  } = useSelector((state) => state.users);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [statusData, setStatusData] = useState({
    is_active: true,
    assigned_districts: [],
    new_districts: [],
  });

  useEffect(() => {
    if (roleList === "district") {
      dispatch(fetchDistrictUsers(pagination));
      dispatch(fetchDistricts());
    }
    if (roleList === "users") {
      dispatch(fetchUsers(pagination));
    }
  }, [dispatch, roleList, pagination.current_page, pagination.pageSize]);

  useEffect(() => {
    if (success) {
      toast({
        title: "Success",
        description: success,
        variant: "default",
      });
      const t = setTimeout(() => {
        dispatch(clearSuccess());
        setEditModalOpen(false);
      }, 2000);
      return () => clearTimeout(t);
    }

    if (error) {
      let errorMsg = "";
      if (error.errors) {
        errorMsg = Object.values(error.errors).flat().join(" | ");
      } else if (error.message) {
        errorMsg = error.message;
      } else {
        errorMsg = "Failed to update user status";
      }

      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });

      dispatch(clearError());
    }
  }, [success, error, dispatch]);

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setStatusData({
      is_active: user.is_active,
      assigned_districts:
        user.assigned_districts?.map((ad) => ({
          id: ad.district_id,
          name: ad.district?.name,
          status: ad.status || "active",
        })) || [],
      new_districts: [],
    });
    setEditModalOpen(true);
  };

  const handleDistrictStatusChange = (districtId, newStatus) => {
    setStatusData((prev) => ({
      ...prev,
      assigned_districts: prev.assigned_districts.map((d) =>
        d.id === districtId ? { ...d, status: newStatus } : d
      ),
    }));
  };

  const handleAddDistricts = (selectedIds) => {
    setStatusData((prev) => ({
      ...prev,
      new_districts: selectedIds,
    }));
  };

  const handleSaveStatus = () => {
    if (!statusData.is_active && statusData.new_districts.length > 0) {
      toast({
        title: "Warning",
        description:
          "Cannot assign new districts to an inactive user. Please activate the user first.",
        variant: "destructive",
      });
      return;
    }

    const updatePayload = {
      user_id: selectedUser.id,
      is_active: statusData.is_active,
      district_assignments: statusData.assigned_districts.map((d) => ({
        district_id: d.id,
        status: d.status,
      })),
      new_districts: statusData.new_districts,
    };

    dispatch(updateUserStatus(updatePayload));
  };

  const finalList = roleList === "district" ? district_users : users;

  const availableDistricts = (districts || [])
    .filter((d) => !statusData.assigned_districts.some((ad) => ad.id === d.id))
    .map((d) => ({
      id: String(d.id),
      name: d.name,
      disabled: false,
    }));

  const handleExport = () => {
    dispatch(
      exportUsers(roleList === "district" ? "district_admin" : "applicant")
    )
      .unwrap()
      .then((res) => {
        const byteCharacters = atob(res.file);
        const byteNumbers = new Array(byteCharacters.length)
          .fill()
          .map((_, i) => byteCharacters.charCodeAt(i));
        const byteArray = new Uint8Array(byteNumbers);

        const blob = new Blob([byteArray], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.setAttribute("download", res.filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(() => {
        toast({
          title: "Error",
          description: "Failed to export users",
          variant: "destructive",
        });
      });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex justify-between items-center mb-4">
            <p>
              {roleList === "district"
                ? "District Office Users List"
                : "All Users List"}
            </p>

            <div className="flex justify-end gap-4">
              <Button variant="outline" onClick={handleExport}>
                Export Users
              </Button>
              {roleList === "district" && (
                <Link to="/admin/users/add">
                  <Button>Add District Officer</Button>
                </Link>
              )}
            </div>
          </CardTitle>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sl.no</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>

                    {roleList == "district" ? (
                      <>
                        <TableHead>Districts Managed</TableHead>
                        <TableHead>Designation</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </>
                    ) : (
                      <>
                        <TableHead>Organization Type</TableHead>
                      </>
                    )}
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {finalList && finalList.length > 0 ? (
                    finalList.map((user, index) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          {(pagination?.current_page - 1) *
                            pagination?.pageSize +
                            (index + 1)}
                        </TableCell>
                        <TableCell>{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.phone}</TableCell>

                        {roleList == "district" ? (
                          <>
                            <TableCell>
                              {user.assigned_districts
                                ?.filter((item) => item.status == "active")
                                .map((item) => item.district?.name)
                                .join(", ") || "-"}
                            </TableCell>
                            <TableCell>{user.designation}</TableCell>
                            <TableCell>
                              <span
                                className={`px-2 py-1 rounded-full text-xs ${
                                  user.is_active
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {user.is_active ? "Active" : "Inactive"}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditClick(user)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </>
                        ) : (
                          <TableCell>{user?.organization_type}</TableCell>
                        )}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center text-muted-foreground py-6"
                      >
                        No users found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <div className="flex justify-end mt-4">
                <Pagination
                  currentPage={pagination.current_page}
                  totalPages={pagination.totalPages}
                  pageSize={pagination.pageSize}
                  onPageChange={(page) =>
                    dispatch(setPagination({ current_page: page }))
                  }
                  onPageSizeChange={(size) =>
                    dispatch(
                      setPagination({
                        pageSize: size,
                        current_page: 1,
                      })
                    )
                  }
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <UserStatusModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        selectedUser={selectedUser}
        statusData={statusData}
        loading={statusUpdating}
        availableDistricts={availableDistricts}
        handleDistrictStatusChange={handleDistrictStatusChange}
        handleAddDistricts={handleAddDistricts}
        setStatusData={setStatusData}
        handleSaveStatus={handleSaveStatus}
      />
    </div>
  );
};

export default UserManagement;
